
/**
 * ScanView Component
 * Shows camera feed with a capture button
 */
import { useRef, useEffect, useState, useCallback } from 'react';
import { cameraService } from '@/services/cameraService';
import { Camera, RefreshCw, CameraOff } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useIsMobile } from '@/hooks/use-mobile';

interface ScanViewProps {
  onImageCaptured: (imageBlob: Blob) => void;
}

export function ScanView({ onImageCaptured }: ScanViewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [cameraStatus, setCameraStatus] = useState<'inactive' | 'starting' | 'pending' | 'active' | 'error'>('inactive');
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const isMobile = useIsMobile();
  
  // Initialize camera
  useEffect(() => {
    let cameraStatusUnsubscribe: (() => void) | null = null;
    
    const initCamera = async () => {
      try {
        setCameraStatus('starting');
        console.log('Camera status: starting');
        
        // Register for camera status changes
        cameraStatusUnsubscribe = cameraService.on('statusChange', (status) => {
          console.log(`Camera status: ${status}`);
          setCameraStatus(status);
          
          if (status === 'error') {
            setCameraError('Failed to access camera');
          }
        });
        
        // Start camera
        const stream = await cameraService.startCamera({
          facingMode: 'environment',
          maxResolution: true
        });
        
        // Attach to video element
        if (videoRef.current) {
          cameraService.attachToVideo(videoRef.current);
          
          // Wait for video to be ready
          videoRef.current.onloadedmetadata = () => {
            if (videoRef.current) {
              videoRef.current.play().catch(err => {
                console.error('Failed to play video:', err);
              });
            }
          };
        }
      } catch (error) {
        console.error('Error starting camera:', error);
        setCameraStatus('error');
        setCameraError('Could not access camera');
        
        toast({
          title: 'Camera Error',
          description: 'Failed to access camera. Please check permissions.',
          variant: 'destructive',
        });
      }
    };
    
    initCamera();
    
    // Clean up resources
    return () => {
      console.log('ScanView: Cleaning up resources');
      
      if (cameraStatusUnsubscribe) {
        cameraStatusUnsubscribe();
      }
      
      // Stop camera if needed - no DOM manipulation here, just service calls
      cameraService.stopCamera();
    };
  }, []);
  
  // Handle capture button click
  const handleCaptureClick = useCallback(async () => {
    if (cameraStatus !== 'active') return;
    
    try {
      setIsCapturing(true);
      
      // Capture the image from camera
      const imageBlob = await cameraService.captureStillImage();
      console.log("Image captured, size:", Math.round(imageBlob.size / 1024), "KB");
      
      // Pass the image blob to parent
      onImageCaptured(imageBlob);
    } catch (error) {
      console.error('Failed to capture image:', error);
      toast({
        title: 'Capture Error',
        description: 'Failed to capture card image',
        variant: 'destructive',
      });
      setIsCapturing(false);
    }
  }, [cameraStatus, onImageCaptured]);
  
  // Handle retry button click
  const handleRetry = useCallback(() => {
    if (cameraStatus === 'error') {
      setCameraStatus('inactive');
      setCameraError(null);
      cameraService.reset();
      
      setTimeout(() => {
        cameraService.startCamera()
          .then(stream => {
            if (videoRef.current) {
              cameraService.attachToVideo(videoRef.current);
            }
          })
          .catch(error => {
            console.error('Failed to restart camera:', error);
            setCameraStatus('error');
            setCameraError('Could not access camera after retry');
          });
      }, 1000);
    }
  }, [cameraStatus]);

  return (
    <div className="flex flex-col items-center w-full max-w-xl mx-auto">
      {/* Camera view with card overlay */}
      <Card className={`relative overflow-hidden bg-black w-full 
        ${isMobile ? 'aspect-[3/4]' : 'aspect-[4/3]'} 
        rounded-lg shadow-xl`}
      >
        {/* Camera feed container */}
        <div 
          ref={containerRef} 
          className="w-full h-full flex items-center justify-center"
        >
          {/* Video element - using ref, not direct DOM manipulation */}
          <video 
            ref={videoRef}
            className="w-full h-full object-cover"
            playsInline
            autoPlay
            muted
          />
          
          {/* Loading state */}
          <AnimatePresence>
            {cameraStatus === 'starting' && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-70 z-20"
              >
                <div className="text-center text-white">
                  <div className="flex justify-center">
                    <RefreshCw className="w-12 h-12 animate-spin text-pokemon-blue" />
                  </div>
                  <motion.p 
                    className="mt-4 text-lg"
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    Starting camera...
                  </motion.p>
                  <motion.p 
                    className="mt-2 text-sm text-gray-300"
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    Please allow camera access when prompted
                  </motion.p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Error state */}
          <AnimatePresence>
            {cameraStatus === 'error' && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-80 z-20"
              >
                <div className="text-center p-6">
                  <motion.div 
                    className="flex justify-center"
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.2, type: "spring" }}
                  >
                    <CameraOff className="w-12 h-12 text-red-500" />
                  </motion.div>
                  <motion.h3 
                    className="mt-4 text-lg font-medium text-white"
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    Camera Error
                  </motion.h3>
                  <motion.p 
                    className="mt-2 text-gray-300"
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    {cameraError || 'Failed to access camera'}
                  </motion.p>
                  <motion.div
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    <Button variant="secondary" className="mt-4" onClick={handleRetry}>
                      Try Again
                    </Button>
                  </motion.div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Card detection overlay */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Scan line animation */}
          <motion.div 
            className="absolute left-[10%] right-[10%] h-1 bg-gradient-to-r from-transparent via-pokemon-blue to-transparent z-10 scan-line"
            initial={{ opacity: 0.7, top: "10%" }}
            animate={{ 
              opacity: [0.4, 0.8, 0.4],
              top: ["10%", "90%", "10%"]
            }}
            transition={{ 
              duration: 4,
              repeat: Infinity,
              ease: "linear"
            }}
          />
          
          {/* Card positioning guide */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className={`${isMobile ? 'w-[80%] h-[60%]' : 'w-[70%] h-[80%]'} 
              border-2 border-dashed border-white/50 rounded-lg flex items-center justify-center`}>
              <div className="text-white/70 text-center p-2 text-sm bg-black/30 rounded">
                Position card here
              </div>
            </div>
          </div>
        </div>
        
        {/* Capture button */}
        <div className="absolute bottom-6 inset-x-0 flex justify-center z-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button 
              onClick={handleCaptureClick}
              size="lg" 
              disabled={cameraStatus !== 'active' || isCapturing}
              className="bg-pokemon-blue hover:bg-pokemon-blue text-white px-8 shadow-lg backdrop-blur-sm"
            >
              {isCapturing ? (
                <>
                  <RefreshCw className="mr-2 h-5 w-5 animate-spin" />
                  Capturing...
                </>
              ) : (
                <>
                  <Camera className="mr-2 h-5 w-5" />
                  Capture Card
                </>
              )}
            </Button>
          </motion.div>
        </div>
      </Card>
    </div>
  );
}
